import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import React, {
  PropsWithChildren,
  RefObject,
  useCallback,
  useRef,
} from "react";
import Map, {
  FullscreenControl,
  MapRef,
  NavigationControl,
} from "react-map-gl/maplibre";
import { MapErrorId, MAPLIBRE_Z_INDEX } from "../../../utils/maps/MapConstants";
import { MapTooltipProvider } from "../context/MapTooltipContext";
import { useMapResize } from "../hooks/useMapResize";
import {
  isRetryableTileStatus,
  TileCoordinates,
  useMapTileRetry,
} from "../hooks/useMapTileRetry";
import RasterTileLayer from "../layers/RasterTileLayer";
import "../mapLibre.css";
import { toSafeLngLat } from "../utils/coordinates";
import { EMPTY_MAP_STYLE } from "../utils/mapStyle";
import { getMapTileTypeFromSourceId } from "../utils/tileSources";

interface MapLibreTileErrorEvent {
  sourceId?: string;
  error?: Error & { status?: number };
  tile?: {
    tileID?: {
      canonical?: TileCoordinates;
    };
  };
}

const MAP_ATTRIBUTIONS = [
  '<a href="https://maplibre.org/" target="_blank">MapLibre</a>',
  "© Microsoft Azure Maps",
];

const DEFAULT_ZOOM = 6;
const MIN_ZOOM = 3;
const MAX_ZOOM = 16;

type MapLibreRootStyle = React.CSSProperties & {
  "--maplibre-control-z-index": number;
  "--maplibre-popup-z-index": number;
};

const MAP_ROOT_STYLE: MapLibreRootStyle = {
  width: "100%",
  height: "100%",
  "--maplibre-control-z-index": MAPLIBRE_Z_INDEX.CONTROL,
  "--maplibre-popup-z-index": MAPLIBRE_Z_INDEX.POPUP,
};

interface MapLibreMapProps {
  center: [number, number];
  zoom?: number;
  minZoom?: number;
  maxZoom?: number;
  baseMapUrl?: string;
  weatherMapUrl?: string;
  showWeather?: boolean;
  mapRef: RefObject<MapRef>;
  onMapApiFailure: (errorId: MapErrorId, isReset?: boolean) => void;
  onAuthError?: () => void;
  testId: string;
}

const MapLibreMap: React.FC<PropsWithChildren<MapLibreMapProps>> = ({
  center,
  zoom = DEFAULT_ZOOM,
  minZoom = MIN_ZOOM,
  maxZoom = MAX_ZOOM,
  baseMapUrl,
  weatherMapUrl,
  showWeather = false,
  mapRef,
  onMapApiFailure,
  onAuthError,
  testId,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scheduleTileRetry, resetTileRetries } = useMapTileRetry(mapRef);

  const handleError = useCallback(
    (event: unknown) => {
      const mapError = event as MapLibreTileErrorEvent;
      const sourceId = mapError.sourceId ?? "";
      const errorId = getMapTileTypeFromSourceId(sourceId);
      if (!errorId) {
        // react-map-gl does not mount its children until MapLibre initializes.
        // Surface initialization/import/WebGL failures instead of silently
        // swallowing them as an event without a sourceId.
        console.error("MapLibre failed to initialize", mapError.error ?? event);
        onMapApiFailure("BASE");
        return;
      }

      onMapApiFailure(errorId);

      const status =
        typeof mapError.error?.status === "number"
          ? mapError.error.status
          : undefined;

      // A new SAS changes the source URL and remounts the source, which causes
      // MapLibre to load the tiles needed by the current viewport again.
      if (status === 401 || status === 403) {
        resetTileRetries(sourceId);
        onAuthError?.();
        return;
      }

      if (isRetryableTileStatus(status)) {
        scheduleTileRetry({
          sourceId,
          status: status as number,
          tile: mapError.tile?.tileID?.canonical,
        });
      }
    },
    [onAuthError, onMapApiFailure, resetTileRetries, scheduleTileRetry]
  );

  const handleSourceData = useCallback(
    (event: unknown) => {
      const sourceEvent = event as {
        sourceId?: string;
        isSourceLoaded?: boolean;
      };
      if (!sourceEvent.isSourceLoaded) {
        return;
      }
      const sourceId = sourceEvent.sourceId ?? "";
      const errorId = getMapTileTypeFromSourceId(sourceId);
      if (errorId) {
        resetTileRetries(sourceId);
        onMapApiFailure(errorId, true);
      }
    },
    [onMapApiFailure, resetTileRetries]
  );

  const [longitude, latitude] = toSafeLngLat(center);
  const resizeMap = useMapResize(containerRef, mapRef);

  return (
    <div
      ref={containerRef}
      className="maplibre-map-root"
      data-testid={testId}
      style={MAP_ROOT_STYLE}
    >
      <Map
        ref={mapRef}
        mapLib={maplibregl}
        initialViewState={{ longitude, latitude, zoom, bearing: 0, pitch: 0 }}
        mapStyle={EMPTY_MAP_STYLE}
        trackResize={false}
        minZoom={minZoom}
        maxZoom={maxZoom}
        maxPitch={60}
        renderWorldCopies={false}
        dragRotate
        touchZoomRotate
        attributionControl={{
          compact: true,
          customAttribution: MAP_ATTRIBUTIONS,
        }}
        onLoad={resizeMap}
        onError={handleError}
        onSourceData={handleSourceData}
        style={{ width: "100%", height: "100%" }}
      >
        <MapTooltipProvider>
          {baseMapUrl && (
            <RasterTileLayer id="BASE" url={baseMapUrl} opacity={0.9} />
          )}

          {showWeather && weatherMapUrl && (
            <RasterTileLayer
              id="WEATHER"
              url={weatherMapUrl}
              opacity={0.8}
              refreshInterval={5 * 60 * 1000}
            />
          )}

          {children}
        </MapTooltipProvider>

        <NavigationControl
          position="bottom-right"
          showCompass
          showZoom
          visualizePitch
        />
        <FullscreenControl position="bottom-right" />
      </Map>
    </div>
  );
};

export default MapLibreMap;
